/**
 * ProgressDistributionChart Component
 * Displays OKR progress distribution as a histogram
 *
 * Story 4.1: OKR Progress Distribution
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card } from '../../common';
import { OrgBaselineNote } from '../ReportScopeIndicator';
import type { ProgressDistributionData } from '../../../services/reportApi';
import type { ResolvedScope } from '../../../hooks/useReportData';

export interface ProgressDistributionChartProps {
  /** Distribution data */
  data: ProgressDistributionData;
  /** Loading state */
  isLoading?: boolean;
  /** Height of the chart */
  height?: number;
  /**
   * Resolved scope for this distribution, so the empty state can say whose
   * OKRs are missing. Resolved by the hook layer.
   */
  scope?: ResolvedScope;
  /** Additional class names */
  className?: string;
}

/**
 * Get bar color based on progress range
 */
function getBarColor(min: number, expectedProgress: number): string {
  if (min >= expectedProgress) {
    return '#22c55e'; // green-500 - on track or ahead
  }
  if (min >= expectedProgress - 20) {
    return '#f59e0b'; // amber-500 - slightly behind
  }
  return '#ef4444'; // red-500 - behind
}

/**
 * Loading skeleton
 */
const DistributionSkeleton: React.FC<{ height: number }> = ({ height }) => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="h-6 bg-stone-200 rounded w-48" />
      <div className="h-6 bg-stone-200 rounded w-24" />
    </div>
    <div style={{ height }} className="bg-stone-100 rounded" />
    <div className="grid grid-cols-3 gap-4 mt-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="text-center">
          <div className="h-8 bg-stone-200 rounded w-16 mx-auto mb-2" />
          <div className="h-4 bg-stone-200 rounded w-20 mx-auto" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Custom tooltip for the chart
 */
const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.[0]) return null;

  const data = payload[0].payload;
  return (
    <div className="bg-white border border-stone-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-stone-900">{data.range}</p>
      <p className="text-sm text-stone-600">
        {data.count} OKRs ({data.percentage.toFixed(1)}%)
      </p>
    </div>
  );
};

/**
 * Progress Distribution Chart Component
 */
export const ProgressDistributionChart: React.FC<ProgressDistributionChartProps> = ({
  data,
  isLoading = false,
  height = 300,
  scope,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <DistributionSkeleton height={height} />
      </Card>
    );
  }

  const { buckets, stats, expectedProgress, okrsBehind, okrsAhead } = data;

  // Nothing to distribute: the histogram would render as an all-zero chart with
  // a "0.0% mean" and a red "vs expected" delta, which describes a failing team
  // rather than an empty one.
  if (stats.total === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900">Progress Distribution</h3>
        <div className="text-center py-12 text-stone-500">
          <Minus className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>
            {scope?.isSelfOnly
              ? 'You own no OKRs in this period'
              : scope?.kind === 'team'
                ? 'Your team owns no OKRs in this period'
                : 'No OKRs in this period'}
          </p>
        </div>
        {data.orgBaseline && (
          <OrgBaselineNote
            className="justify-center"
            value={`${data.orgBaseline.mean.toFixed(1)}%`}
            label={`mean progress across ${data.orgBaseline.total} OKRs`}
          />
        )}
      </Card>
    );
  }

  // Determine overall trend
  const progressDiff = stats.mean - expectedProgress;
  const TrendIcon = progressDiff > 5 ? TrendingUp : progressDiff < -5 ? TrendingDown : Minus;
  const trendColor = progressDiff > 5 ? 'text-green-600' : progressDiff < -5 ? 'text-red-600' : 'text-stone-600';

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">Progress Distribution</h3>
          <p className="text-sm text-stone-500">OKR progress across all objectives</p>
        </div>
        <div className={clsx('flex items-center gap-1', trendColor)}>
          <TrendIcon className="w-4 h-4" />
          <span className="text-sm font-medium">
            {progressDiff > 0 ? '+' : ''}{progressDiff.toFixed(1)}% vs expected
          </span>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={buckets}
            margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
            <XAxis
              dataKey="range"
              tick={{ fontSize: 12, fill: '#78716c' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 12, fill: '#78716c' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              x={`${Math.floor(expectedProgress / 10) * 10}-${Math.floor(expectedProgress / 10) * 10 + 10}%`}
              stroke="#6366f1"
              strokeDasharray="5 5"
              label={{
                value: 'Expected',
                position: 'top',
                fill: '#6366f1',
                fontSize: 12,
              }}
            />
            <Bar
              dataKey="count"
              radius={[4, 4, 0, 0]}
            >
              {buckets.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getBarColor(entry.min, expectedProgress)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-4 border-t border-stone-200">
        <div className="text-center">
          <p className="text-2xl font-bold text-stone-900">{stats.mean.toFixed(1)}%</p>
          <p className="text-xs text-stone-500">Mean Progress</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-stone-900">{stats.median.toFixed(1)}%</p>
          <p className="text-xs text-stone-500">Median Progress</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-red-600">{okrsBehind}</p>
          <p className="text-xs text-stone-500">Behind Schedule</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">{okrsAhead}</p>
          <p className="text-xs text-stone-500">Ahead of Schedule</p>
        </div>
      </div>

      {/* Organization comparison for a scoped caller (absent for an admin) */}
      {data.orgBaseline && (
        <OrgBaselineNote
          className="justify-center mt-4"
          value={`${data.orgBaseline.mean.toFixed(1)}%`}
          label={`mean progress across ${data.orgBaseline.total} OKRs`}
        />
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500" />
          <span className="text-xs text-stone-600">On Track</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-amber-500" />
          <span className="text-xs text-stone-600">Slightly Behind</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-red-500" />
          <span className="text-xs text-stone-600">Behind</span>
        </div>
      </div>
    </Card>
  );
};

export default ProgressDistributionChart;
